import Anchor from 'components/Content/Anchor';
import Banner from 'components/Content/Banner';
import BannerList from 'components/Content/BannerList';
import Copy from 'components/Content/Copy';
import Divider from 'components/Content/Divider';
import ProductList from 'components/Content/ProductList';
import PromotionList from 'components/Content/PromotionList';
import PromotionListForYouHandler from 'components/Content/PromotionListHandler/PromotionListForYouHandler';
import PromotionListFeaturedOffersHandler from 'components/Content/PromotionListHandler/PromotionListFeaturedOffersHandler';
import PromotionListExclusiveOffersHandler from 'components/Content/PromotionListHandler/PromotionListExclusiveOffersHandler';
import PromotionListTrendingOffersHandler from 'components/Content/PromotionListHandler/PromotionListTrendingOffersHandler';
import Recap from 'components/Content/Recap';
import RecapSYSHandler from 'components/Content/RecapHandler/RecapSYSHandler';
import RewardList from 'components/Content/RewardList';
import SectionHeading from 'components/Content/SectionHeading';
import SoftLinks from 'components/Content/SoftLinks';
import SMSOptIn from 'components/Content/SMSOptIn';
import Section from 'components/Section';
import SearchBarHandler from 'components/Content/SectionHandler/SearchBarHandler';
import CustomRendering from 'components/Content/CustomRendering';
import UgcWidget from 'components/Content/UgcWidget';
import LovesList from 'components/Content/LovesList';
import contentConstants from 'constants/content';

const { COMPONENT_TYPES, COMPONENT_HANDLER_TYPES } = contentConstants;

const cmsComponentMapping = {
    [COMPONENT_TYPES.ANCHOR]: Anchor,
    [COMPONENT_TYPES.BANNER_LIST]: BannerList,
    [COMPONENT_TYPES.BANNER]: Banner,
    [COMPONENT_TYPES.COPY]: Copy,
    [COMPONENT_TYPES.DIVIDER]: Divider,
    [COMPONENT_TYPES.PRODUCT_LIST]: ProductList,
    [COMPONENT_TYPES.PROMOTION_LIST]: PromotionList,
    [COMPONENT_TYPES.RECAP]: Recap,
    [COMPONENT_TYPES.REWARD_LIST]: RewardList,
    [COMPONENT_TYPES.SECTION_HEADING]: SectionHeading,
    [COMPONENT_TYPES.SOFT_LINKS]: SoftLinks,
    [COMPONENT_TYPES.SMS_OPTIN]: SMSOptIn,
    [COMPONENT_TYPES.SECTION]: Section,
    [COMPONENT_TYPES.CUSTOM_RENDERING]: CustomRendering,
    [COMPONENT_TYPES.UGC_WIDGET]: UgcWidget,
    [COMPONENT_TYPES.LOVES_LIST]: LovesList
};

const cmsComponentHandlerTypeMapping = {
    [COMPONENT_TYPES.PROMOTION_LIST]: {
        [COMPONENT_HANDLER_TYPES.PROMOTION_LIST.TRENDING_OFFERS]: PromotionListTrendingOffersHandler,
        [COMPONENT_HANDLER_TYPES.PROMOTION_LIST.FOR_YOU]: PromotionListForYouHandler,
        [COMPONENT_HANDLER_TYPES.PROMOTION_LIST.FEATURED_OFFERS]: PromotionListFeaturedOffersHandler,
        [COMPONENT_HANDLER_TYPES.PROMOTION_LIST.EXCLUSIVE_OFFERS]: PromotionListExclusiveOffersHandler
    },
    [COMPONENT_TYPES.RECAP]: {
        [COMPONENT_HANDLER_TYPES.RECAP.RECAP_SYS]: RecapSYSHandler
    },
    [COMPONENT_TYPES.SECTION]: {
        [COMPONENT_HANDLER_TYPES.SECTION.SEARCH_BAR]: SearchBarHandler
    }
};

// Some handlers only apply under specific conditions; the rest are catch-alls (always apply).
// Trending is anonymous-only and flag-gated. Eligibility is evaluated here so the router can pick
// the highest-priority *eligible* handler present in features, falling through to the next one when
// a handler does not apply (e.g. Trending → For-You once the user signs in).
const handlerConstraints = {
    [COMPONENT_HANDLER_TYPES.PROMOTION_LIST.TRENDING_OFFERS]: ({ isAnonymous, personalization }) =>
        isAnonymous && Sephora.configurationSettings?.p13ntrendingNBO?.isEnabled && personalization?.isTrendingOffersEnabled
};

// `isAnonymous` is passed in (not read from the store) so routing doesn't depend on Redux state.
// Note: this still depends on global runtime config (Sephora.configurationSettings).
// ComponentList feeds it the reactive auth value and re-keys the promo list on auth change, which
// remounts the lazy subtree so the newly-resolved handler is actually mounted (see ComponentList).
const getCmsComponent = ({ type, features = [], isAnonymous = false, personalization }) => {
    const handlerMapping = cmsComponentHandlerTypeMapping[type];

    if (handlerMapping && Array.isArray(features) && features.length) {
        const present = new Set(features.map(f => f?.handlerType));
        // Candidates present in features, in mapping priority order (Trending first).
        const candidates = Object.keys(handlerMapping).filter(handlerType => present.has(handlerType));
        // First candidate whose constraint holds → falls through to the next available handler.
        const resolved = candidates.find(handlerType => {
            const constraint = handlerConstraints[handlerType];

            return !constraint || constraint({ isAnonymous, personalization });
        });

        if (resolved) {
            return handlerMapping[resolved];
        }
    }

    return cmsComponentMapping[type];
};

export {
    cmsComponentMapping, getCmsComponent
};
